// Copyright (c) Docugami, Inc. All rights reserved.

import { ComponentId } from "sdv/engine/Component";
import { IEntityManager } from "sdv/engine/Entity";
import { ISystem, Queries } from "../engine/Engine";
import { IResources } from "../Resources";
import { InputState } from "sdv/input/InputController";
import { Point } from "sdv/Types";
import { worldToViewSpace } from "sdv/Space";
import { MutableRefObject } from "react";
import { Box, boxesIntersect, nestedBox } from "store/shared_types";
import { ExternalRenderComponent } from "sdv/components/ExternalRenderComponent";
import { PositionComponent } from "sdv/components/PositionComponent";
import { ViewComponent } from "sdv/components/ViewComponent";

/**
 * Updates the visibility and positioning of components rendering externally by React
 * but owned by the Engine.
 */
export class ExternalRenderSystem implements ISystem {
    private readonly _canvas: MutableRefObject<HTMLElement | undefined>;

    get updateEnabled() { return true; }

    resources!: IResources;
    entities!: IEntityManager;
    input!: InputState;

    queries: Queries = {
        externals: { types: [ComponentId.Position, ComponentId.ExternalRender, ComponentId.Renderable] },
        view: { types: [ComponentId.View] }
    }

    constructor(canvas: MutableRefObject<HTMLElement | undefined>) {
        this._canvas = canvas;
    }

    update() {
        this.updateExternalRenderablePositions()
    }

    private updateExternalRenderablePositions() {
        if (!this._canvas.current) {
            return;
        }

        const view = this.queries.view.query!.single()?.getComponent<ViewComponent>(ComponentId.View);
        if (!view) {
            return;
        }

        if (!this.queries.externals.query?.any()) {
            return;
        }

        const viewPosition = view.viewport.worldPosition;
        const viewWidth = view.viewport.worldWidth;
        const viewHeight = view.viewport.worldHeight;

        const viewBox: Box = {
            left: viewPosition.x,
            right: viewPosition.x + viewWidth,
            top: viewPosition.y,
            bottom: viewPosition.y + viewHeight,
            page: -1
        }

        const canvasBounds = this._canvas.current.getBoundingClientRect();
        for (const entity of this.queries.externals.query!.entities) {
            const positionComp = entity.getComponent<PositionComponent>(ComponentId.Position)!;
            const renderComp = entity.getComponent<ExternalRenderComponent>(ComponentId.ExternalRender)!;
            if (!renderComp.element?.current) {
                continue;
            }

            const position: Point = { x: positionComp.x, y: positionComp.y };
            if (!!renderComp.size) {
                const entityBox: Box = {
                    left: position.x,
                    right: position.x + renderComp.size.width,
                    top: position.y,
                    bottom: position.y + renderComp.size.height,
                    page: -1
                }

                if (!boxesIntersect(entityBox, viewBox)) {
                    // Not visible
                    renderComp.isVisible = false;
                    renderComp.element.current.style.display = 'none';
                    continue;
                }

                // We currently clip any rendered element to the canvas area
                // Adjust external renderables if they are overlapping the canvas on any axis
                adjustBoxToBounds(entityBox, viewBox);

                // set position dimensions after making adjustments
                position.x = entityBox.left;
                position.y = entityBox.top;
            }

            const positionViewSpace = worldToViewSpace(position, viewPosition);
            const positionGlobalSpace: Point = {
                x: positionViewSpace.x + canvasBounds.x,
                y: positionViewSpace.y + canvasBounds.y
            }

            renderComp.isVisible = true;
            renderComp.element.current.style.display = '';
            renderComp.element.current.style.position = 'fixed';
            renderComp.element.current.style.left = `${positionGlobalSpace.x}px`;
            renderComp.element.current.style.top = `${positionGlobalSpace.y}px`;
        }
    }
}

function adjustBoxToBounds(box: Box, bounds: Box) {
    if (!nestedBox(box, bounds)) {
        const width = box.right - box.left;
        const height = box.bottom - box.top;

        // Ensure box does not cross over x-axis
        box.right = Math.min(box.right, bounds.right);
        box.left = Math.max(Math.min(box.left, box.right - width), bounds.left);

        // Ensure box does not cross over y-axis
        box.bottom = Math.min(box.bottom, bounds.bottom);
        box.top = Math.max(Math.min(box.top, box.bottom - height), bounds.top);
    }
}
